import { useRef, useState } from "react";
import { ThumbsUp } from "lucide-react";
import { useMsal } from "@azure/msal-react";
import { useGraphToken } from "../auth/useGraphToken";
import type { LibraryItem } from "../types/navigator";
import { updateLibraryLikes } from "../api/graph";

function likedKey(userId: string, itemId: string) {
  return `ai-navigator:liked:${userId.toLowerCase()}:${itemId}`;
}

function readLiked(key: string): boolean {
  try { return localStorage.getItem(key) === "1"; } catch { return false; }
}

function writeLiked(key: string, value: boolean) {
  try { localStorage.setItem(key, value ? "1" : "0"); } catch { /* quota exceeded — ignore */ }
}

type Props = { item: LibraryItem };

export function LikeButton({ item }: Props) {
  const { instance, accounts } = useMsal();
  const userId = (instance.getActiveAccount() ?? accounts[0])?.username ?? "";
  const getToken = useGraphToken();

  // Freeze the key at mount time so the initial `liked` state and all
  // localStorage writes use the same key, even if userId updates mid-session.
  const keyRef = useRef(likedKey(userId, item.id));

  const [liked, setLiked] = useState(() => readLiked(keyRef.current));
  const [count, setCount] = useState(item.likes);
  const [busy, setBusy] = useState(false);

  const doLike = async () => {
    const newLiked = !liked;
    const newCount = newLiked ? count + 1 : Math.max(0, count - 1);

    // Apply optimistic update + persist liked state immediately
    setLiked(newLiked);
    setCount(newCount);
    writeLiked(keyRef.current, newLiked);

    setBusy(true);
    try {
      const token = await getToken();
      await updateLibraryLikes(token, item.id, newCount);
    } catch (err) {
      // Log so the actual error (field name mismatch, permission, etc.) is visible
      console.error("[LikeButton] SharePoint PATCH failed for item", item.id, err);
      // Keep the liked state — the user's intent is preserved in localStorage.
      // The count will self-correct on the next page load from SharePoint.
    } finally {
      setBusy(false);
    }
  };

  const handleClick = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (busy) return;
    void doLike();
  };

  return (
    <span
      role="button"
      tabIndex={0}
      className={`like-btn${liked ? " is-liked" : ""}${busy ? " is-busy" : ""}`}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleClick(e); }}
      aria-label={liked ? "Unlike this item" : "Like this item"}
      aria-pressed={liked}
    >
      <ThumbsUp size={18} aria-hidden />
      {count > 0 ? <span className="like-count">{count}</span> : null}
    </span>
  );
}
